import { Box, Center, Group, Modal, Progress, Text, UnstyledButton } from '@mantine/core';
import { getDocument, GlobalWorkerOptions, PageViewport, PDFDocumentProxy } from 'pdfjs-dist';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';

import { getEmbeddingFile } from '@mygpt/app/chat/services';
import { Footnote } from '@mygpt/types';

interface PdfViewerProps {
  fileId: string;
  pageNumbers: number[]; // 初始化页码
  referencePage?: Footnote[]; // 所有用到的引用
  onClose: () => void;
}

interface ProgressDataParams {
  loaded: number;
  total: number;
}

GlobalWorkerOptions.workerSrc = 'https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js';

const PdfViewer: React.FC<PdfViewerProps> = ({ fileId, onClose, pageNumbers, referencePage }) => {
  const { t } = useTranslation();

  const viewerRef = useRef<HTMLDivElement>(null);
  const viewerWrapRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [progressData, setProgressData] = useState<ProgressDataParams>({ loaded: 0, total: 0 });

  const [errorText, setErrorText] = useState('');

  const progress = useMemo(() => {
    const { loaded, total } = progressData;
    if (!loaded || !total) return 0;
    return (loaded / total) * 100;
  }, [progressData]);

  const fileUrl = useMemo(() => {
    if (!fileId) return '';
    return getEmbeddingFile({ file_id: fileId, to_pdf: true });
  }, [fileId]);

  const resetData = () => {
    setLoading(true);
    setProgressData({ loaded: 0, total: 0 });
    setErrorText('');
  };

  useEffect(() => {
    if (!fileUrl) {
      resetData();
      return;
    }
    const loadingTask = getDocument({
      url: fileUrl,
      cMapUrl: 'https://unpkg.com/pdfjs-dist@3.11.174/cmaps/',
      cMapPacked: true,
    });

    loadingTask.onProgress = (progressData: ProgressDataParams) => {
      setProgressData(progressData);
    };

    const containerMap = new Map<number, HTMLDivElement>();
    let pdf: PDFDocumentProxy | null = null;
    let baseViewport: PageViewport | null = null;
    const createCanvas = async (pageNum: number) => {
      if (!pdf || !baseViewport) return;
      const page = await pdf.getPage(pageNum);
      // 创建一个 canvas 元素用于渲染每一页
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) {
        console.error('get context error');
        return;
      }
      canvas.width = baseViewport.width;
      canvas.height = baseViewport.height;
      const renderContext = {
        canvasContext: context,
        viewport: baseViewport,
      };
      Promise.resolve().then(() => {
        page.render(renderContext);
      });
      return canvas;
    };

    const renderPage = async (pageNum: number) => {
      const canvas = await createCanvas(pageNum);
      if (canvas) {
        const container = containerMap.get(pageNum);
        if (container && container.firstElementChild) {
          container.firstElementChild.innerHTML = '';
          container.firstElementChild.appendChild(canvas);
        }
      }
    };

    let observer: IntersectionObserver | null = null;
    const handleScroll: IntersectionObserverCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const page = entry.target;
          if (!(page instanceof HTMLElement)) return;
          const pageId = page.id.split('-')[1];
          const canvas = page.querySelector('canvas');
          if (!canvas) {
            renderPage(Number(pageId));
            observer?.unobserve(page);
          }
        }
      });
    };

    const runTask = async () => {
      try {
        pdf = await loadingTask.promise;
        setLoading(false);

        const numPages = pdf.numPages;

        // 清空容器
        if (viewerRef.current) {
          viewerRef.current.innerHTML = '';
        }
        // 当传入页数大于当前文档最大页数时，加载第一页
        const initialPageNumber = pageNumbers[0] > numPages ? 1 : pageNumbers[0];
        const basePage = await pdf.getPage(initialPageNumber || 1);
        const scale = (window.innerWidth - 40) / basePage.getViewport({ scale: 1 }).width; // 使用窗口的宽度来确定缩放比例
        baseViewport = basePage.getViewport({ scale: scale > 1.5 ? 1.5 : scale });

        const createContainer = (pageNum: number) => {
          const div = document.createElement('div');
          div.id = `page-${pageNum}`;
          ReactDOM.render(
            <>
              <div
                className="w-full h-full text-center flex items-center justify-center bg-white shadow-lg"
                style={{ width: baseViewport?.width, height: baseViewport?.height }}
              >
                <Loader />
              </div>
              <Driver pageNum={pageNum} />
            </>,
            div
          );
          return div;
        };
        observer = new IntersectionObserver(handleScroll, {
          threshold: 0.05,
          root: viewerWrapRef.current,
        });
        // 迭代并渲染所有页面
        for (let pageNum = 1; pageNum <= numPages; pageNum++) {
          const container = createContainer(pageNum);
          containerMap.set(pageNum, container);
          if (viewerRef.current) {
            viewerRef.current.appendChild(container);
            observer.observe(container);
          }
        }
        scrollToPage(pageNumbers[0]);
      } catch (error) {
        // PDF loading error
        console.error(error);
        let errMsg = (error as Error).message;
        if (errMsg && errMsg.startsWith('Invalid PDF structure')) {
          errMsg = t('errors.detail.source_file_type_limit');
        }
        setErrorText(errMsg);
      }
    };
    runTask();

    return () => {
      if (loadingTask && loadingTask.destroy) {
        loadingTask.destroy();
      }
      observer?.disconnect();
      for (const container of containerMap.values()) {
        ReactDOM.unmountComponentAtNode(container);
      }
    };
  }, [fileUrl, pageNumbers, t]);

  const scrollToPage = (page: number) => {
    const targetCanvas = document.getElementById(`page-${page}`);
    targetCanvas?.scrollIntoView();
  };

  const getFileTitle = () => {
    const file = referencePage?.find((item) => item.file_id === fileId);
    return file ? file.title : '';
  };

  return (
    <Modal
      opened={!!fileId}
      onClose={onClose}
      centered
      transitionProps={{
        transition: 'fade',
        duration: 600,
        timingFunction: 'ease',
      }}
      size={'auto'}
      padding={0}
      classNames={{
        header: 'p-4 m-0 bg-black/70 text-white',
        close: 'text-white hover:bg-black/50',
      }}
      title={getFileTitle()}
      zIndex={500}
    >
      <Box
        className="flex flex-col relative bg-black/40"
        mah={'75vh'}
        miw={'50vw'}
        style={{ background: loading ? 'white' : undefined }}
      >
        <section className="flex-auto overflow-y-auto" ref={viewerWrapRef}>
          <Center mih={300} className="flex flex-col">
            {loading && !errorText && (
              <>
                <Progress w={'80%'} radius="xs" size="sm" value={progress} striped animated />
                <Center className="text-gray-400 text-xs mt-4">
                  {progress === 0 ? t('pdf_viewer.converting') : `${t('pdf_viewer.loading')}${progress.toFixed(2)}%`}
                </Center>
              </>
            )}
            {errorText && (
              <Text c={'violet.5'} mx={100}>
                {errorText}
              </Text>
            )}
            <div className="flex flex-col items-center justify-center" ref={viewerRef} />
          </Center>
        </section>
        {!loading && (
          <section className="flex-none bg-transparent p-4 absolute left-0 bottom-0">
            <Group>
              {referencePage?.map((item, index) => {
                const page_numbers = item.page_numbers;
                const firstPage = page_numbers[0];
                const page_text =
                  page_numbers && page_numbers.length > 1
                    ? `${page_numbers[0]}-${page_numbers[page_numbers.length - 1]}`
                    : page_numbers[0];
                return (
                  <UnstyledButton key={item.id} onClick={() => scrollToPage(firstPage)}>
                    <span
                      className={`px-1 leading-6 bg-gray-100 text-gray-500 opacity-50 hover:opacity-100 rounded-md text-xs no-underline flex whitespace-nowrap cursor-pointer`}
                    >
                      <span className="text-ellipsis overflow-hidden max-w-[240px]">{`${index + 1}. ${t(
                        'pdf_viewer.page_of',
                        { page: page_text }
                      )}`}</span>
                    </span>
                  </UnstyledButton>
                );
              })}
            </Group>
          </section>
        )}
      </Box>
    </Modal>
  );
};

const Loader = () => (
  <svg
    className="animate-spin -ml-1 mr-3 h-5 w-5 text-indigo-600"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    ></path>
  </svg>
);

const Driver = ({ pageNum }: { pageNum: number }) => {
  const { t } = useTranslation();

  return (
    <div className="flex items-center py-2">
      <div className="flex-1 border-dotted border-t-2 border-gray-200"></div>
      <span className="px-3 text-white text-xs">{t('pdf_viewer.page_of', { page: pageNum })}</span>
      <div className="flex-1 border-dotted border-t-2 border-gray-200"></div>
    </div>
  );
};

export default PdfViewer;
